import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const navigation = [
    { id: "home", label: "Home", href: "/" },
    { id: "about", label: "About", href: "/about" },
    { id: "services", label: "Services", href: "/services" },
    { id: "contact", label: "Contact", href: "/contact" },
  ];

  const isActive = (href: string) => {
    if (href === "/" && location.pathname === "/") return true;
    if (href !== "/" && location.pathname.startsWith(href)) return true;
    return false;
  };

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      role="banner"
      className={`fixed top-0 z-50 w-full transition-all duration-300 ${
        scrolled
          ? "bg-[#1e1642]/95 backdrop-blur-md border-b border-white/10 shadow-lg shadow-black/20"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-4">
        <div className="flex h-[68px] items-center justify-between">

          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-3"
            aria-label="Northstart Systems Home"
          >
            <img
              src="/images/logo.png"
              alt="Northstart Systems Logo"
              className="h-10 w-auto object-contain" 
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            <nav className="flex items-center gap-1" aria-label="Main navigation">
              {navigation.map((item) => (
                <Link
                  key={item.id}
                  to={item.href}
                  aria-current={isActive(item.href) ? "page" : undefined}
                  className={`px-4 py-2 rounded-full text-sm font-semibold tracking-wide transition-all duration-200 ${
                    isActive(item.href)
                      ? "bg-indigo-600 text-white"
                      : scrolled
                      ? "text-white/60 hover:text-white hover:bg-white/10"
                      : "text-gray-800 hover:text-indigo-600 hover:bg-indigo-50"
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>

            <Link
              to="/contact"
              className={`ml-4 px-5 py-2 rounded-full border text-sm font-semibold transition-all duration-200 ${
                scrolled
                  ? "border-white/25 text-white/85 hover:bg-white/10 hover:border-white/40"
                  : "border-indigo-600 text-indigo-600 hover:bg-indigo-600 hover:text-white"
              }`}
            >
              Get in touch
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className={`md:hidden flex items-center justify-center w-9 h-9 rounded-lg border transition ${
              scrolled
                ? "border-white/15 bg-white/5 hover:bg-white/10"
                : "border-gray-200 bg-white hover:bg-gray-50"
            }`}
            onClick={() => setIsMenuOpen((v) => !v)}
            aria-label="Toggle menu"
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? (
              <X className={`h-5 w-5 ${scrolled ? "text-white/70" : "text-gray-700"}`} aria-hidden="true" />
            ) : (
              <Menu className={`h-5 w-5 ${scrolled ? "text-white/70" : "text-gray-700"}`} aria-hidden="true" />
            )}
          </button>
        </div>

        {/* Mobile Dropdown */}
        {isMenuOpen && (
          <nav
            className={`md:hidden pb-3 border-t transition-colors duration-300 ${
              scrolled ? "border-white/10 bg-[#1e1642]/95" : "border-gray-100 bg-white"
            }`}
            aria-label="Mobile navigation"
          >
            <div className="flex flex-col gap-1 pt-2">
              {navigation.map((item) => (
                <Link
                  key={item.id}
                  to={item.href}
                  aria-current={isActive(item.href) ? "page" : undefined}
                  className={`block rounded-xl px-4 py-3 text-sm font-semibold transition ${
                    isActive(item.href)
                      ? "bg-indigo-600 text-white"
                      : scrolled
                      ? "text-white/60 hover:text-white hover:bg-white/8"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  {item.label}
                </Link>
              ))}
              <Link
                to="/contact"
                className={`mt-1 block rounded-xl px-4 py-3 text-sm font-semibold text-center border transition ${
                  scrolled
                    ? "border-white/15 text-white/60 hover:bg-white/8"
                    : "border-indigo-600 text-indigo-600 hover:bg-indigo-50"
                }`}
              >
                Get in touch
              </Link>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;